import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { LoadErrorPage } from '../../../shell/LoadErrorPage';
import {
  SessionProvider,
  isUnauthenticated,
  nextRouteFor,
  useSessionQuery,
  type SessionSetup,
} from '../../../lib/session';

const wizardPath: Record<SessionSetup['kind'], string> = {
  organization: '/setup/organization',
  user: '/setup/user',
};

export interface RequireSetupProps {
  fallback: ReactNode;
  /** The wizard this route belongs to: a setup session of the other kind is sent to its own wizard. */
  kind?: SessionSetup['kind'] | undefined;
  children?: ReactNode | undefined;
}

/**
 * Guard of the wizard routes: a setup session (signed in, 2FA not yet set up, onboarding step `security` open) or a
 * full session may pass; no session → `/login`; a session that still owes its 2FA code → the 2FA screens.
 */
export function RequireSetup({ fallback, kind, children }: RequireSetupProps) {
  const location = useLocation();
  const query = useSessionQuery();
  if (query.isPending) return <>{fallback}</>;
  if (query.isError) {
    if (isUnauthenticated(query.error)) {
      return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
    }
    return <LoadErrorPage error={query.error} onRetry={() => void query.refetch()} />;
  }
  const session = query.data;
  const setup = session.setup;
  if (setup) {
    if (kind && setup.kind !== kind) return <Navigate to={wizardPath[setup.kind]} replace />;
  } else {
    const next = nextRouteFor(session);
    if (next !== null) return <Navigate to={next} replace />;
    if (session.active === null) return <Navigate to="/login" replace />;
  }
  return <SessionProvider session={session}>{children ?? <Outlet />}</SessionProvider>;
}
